import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import Auth from "./views/Auth/Auth";
import Dashboard from "./views/Dashboard";
import CapteurDetails from "./CapteurDetails/CapteurDetails";
import { auth, dashboard, objDetail } from "./references/link";
import NoConnection from "./views/NoConnection";
import { LanguageProvider } from "./LanguageContext";
import liste_des_langues from "./ressources/liste_des_langues";
import sendPostRequest from "./ressources/tools/sendPostRequest";
import { liste_des_capteurs_en_fontion_de_la_proximite } from "./references/glossaire_requetes";

const RedirectWithId = ({ to }) => {
  const { id } = useParams();
  return <Navigate replace to={to.replace(":id", id)} />;
};
export const isInIframe = window.self !== window.top;

function App() {
  const [loading, setLoading] = useState(false);
  const [login_name, setLogin_name] = useState(null);
  const [login_password, setLogin_password] = useState(null);
  const [iframeLogin, setIframeLogin] = useState(false);
  const [iframeLoginMessage, setIframeLoginMessage] = useState(0);
  const [originPath] = useState(window.location.pathname);
  const [forcedRefresh, setForcedRefresh] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const receiveMessage = (event) => {
      setIframeLoginMessage(iframeLoginMessage + 1);
      if (event.data.type === "auth") {
        setLogin_name(event.data.login_name);
        setLogin_password(event.data.login_password);
        setIframeLogin(true);
      }
    };
    if (iframeLogin) {
      window.removeEventListener("message", receiveMessage);
    } else {
      window.addEventListener("message", receiveMessage);
    }

    return () => {
      window.removeEventListener("message", receiveMessage);
    };
  }, [iframeLogin]);

  useEffect(() => {
    if (window.location.pathname !== originPath && !forcedRefresh) {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker
          .register("/service-worker.js", { scope: "/" })
          .then((registration) => {
            // registration worked
            console.log("Registration succeeded.");
            if (document.domain !== "carboparc-dev.itmsol.fr") {
              registration.update();
              window.location.reload();
            }
          })
          .catch((error) => {
            // registration failed
            console.error(`Registration failed with ${error}`);
          });
      }
    }
    setForcedRefresh(true);
  }, []);

  useEffect(() => {
    if (login_name && login_password) {
      reqConnexion(login_name, login_password);
    }
  }, [login_name, login_password]);

  const reqConnexion = (box_login_name, box_login_password) => {
    let sendLogin_name = box_login_name;
    let sendLogin_password = box_login_password;
    setLoading(true);
    if (login_name && login_password) {
      sendLogin_name = login_name;
      sendLogin_password = login_password;
    }

    // Vérifie si l'application est chargée dans un iframe

    if (isInIframe) {
      sendConnexion(null, null, sendLogin_name, sendLogin_password);
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          sendConnexion(
            latitude,
            longitude,
            sendLogin_name,
            sendLogin_password
          );
        },
        (error) => {
          console.warn("Erreur de géolocalisation :", error);
          sendConnexion(null, null, sendLogin_name, sendLogin_password);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }
  };

  const sendConnexion = (
    latitude,
    longitude,
    sendLogin_name,
    sendLogin_password
  ) => {
    sendPostRequest({
      request_type: liste_des_capteurs_en_fontion_de_la_proximite,
      coordinates: { x: latitude, y: longitude },
      login_name: sendLogin_name,
      login_password: sendLogin_password,
    }).then((res) => {
      setLoading(false);
      if (res && res["state"] === true) {
        setIsAuth(true);
        // Mettre à jour le localStorage chaque fois que la langue change
        localStorage.setItem(
          auth,
          JSON.stringify({
            login_name: sendLogin_name,
            login_password: sendLogin_password,
          })
        );
        setData(res["machines_list"]);

        if (originPath === `/${defaultLang}/${auth}` || originPath === `/`) {
          navigate(`/${defaultLang}/${dashboard}`);
        } else if (
          originPath !== `/${defaultLang}/${auth}` &&
          originPath !== "/"
        ) {
          navigate(originPath);
        }
      } else {
        alert("Connexion échouée. Veuillez vérifier vos identifiants.");
        navigate(`/${defaultLang}/${auth}`);
      }
    });
  };

  let defaultLang = "fr";
  let initialLang = window.location.pathname.split("/")[1];
  const lang = initialLang;
  const navigate = useNavigate();
  if (liste_des_langues.indexOf(initialLang) < 0) {
    initialLang = localStorage.getItem("language") || defaultLang;
  }

  useEffect(() => {
    localStorage.setItem("language", initialLang);

    if (window.location.pathname.split("/")[1] !== initialLang) {
      navigate(`/${initialLang}`);
    }
  }, [initialLang, navigate]);

  if (liste_des_langues.indexOf(lang) > -1) {
    defaultLang = initialLang;
  }
  const [data, setData] = useState([]);
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    let initialAuth = JSON.parse(localStorage.getItem(auth));
    if (
      !isAuth &&
      initialAuth &&
      initialAuth.login_name &&
      initialAuth.login_password
    ) {
      reqConnexion(initialAuth.login_name, initialAuth.login_password);
    } else {
      navigate(`/${defaultLang}/${auth}`);
    }
  }, []);

  if (!isOnline) {
    return <NoConnection />;
  }

  return (
    <LanguageProvider navigate={navigate} currentLanguage={defaultLang}>
      <Routes>
        <Route
          path={`/:lang/${auth}`}
          element={
            <Auth
              reqConnexion={reqConnexion}
              isAuth={isAuth}
              loading={loading}
              setIsAuth={setIsAuth}
            />
          }
        />
        <Route
          path={`/:lang/${dashboard}`}
          element={
            <Dashboard
              data={data}
              defaultLang={defaultLang}
              loading={loading}
            />
          }
        />
        <Route path={`/:lang/${objDetail}/:id`} element={<CapteurDetails />} />
        <Route
          path={`/:lang`}
          element={
            <Auth
              reqConnexion={reqConnexion}
              isAuth={isAuth}
              loading={loading}
              setIsAuth={setIsAuth}
            />
          }
        />
        <Route
          path="/"
          element={<Navigate replace to={`/${defaultLang}/${auth}`} />}
        />
        <Route
          path={`/${auth}`}
          element={<Navigate replace to={`/${defaultLang}/${auth}`} />}
        />
        <Route
          path={`/${dashboard}`}
          element={<Navigate replace to={`/${defaultLang}/${dashboard}`} />}
        />
        <Route
          path={`/${objDetail}/:id`}
          element={<RedirectWithId to={`/${defaultLang}/${objDetail}/:id`} />}
        />
      </Routes>
    </LanguageProvider>
  );
}

export default App;
